import type { DictionarySource } from '../features/editChecks/dictionaryChecksDisplay';

export interface EditCheckSpec {
  id: string;
  name: string;
  crfForm: string;
  fieldName: string;
  fieldLabel: string;
  category: 'endpoint' | 'safety' | 'protocol_compliance' | 'general';
  checkType: string;
  timing: 'front_end' | 'back_end' | 'both';
  severity: 'error' | 'warning' | 'query';
  rule: string;
  technicalRule: string;
  queryMessage: string;
  protocolReference: string;
  relatedFields?: string[];
  relatedForms?: string[];
  active: boolean;
  version: string;
  /**
   * 'dictionary' when written from the study's uploaded dictionary (D7);
   * 'family' when written by rule from the study facts (docgen ECS).
   */
  source?: 'dictionary' | 'family' | 'default_range';
  /**
   * 'default' on a standard plausibility range written because no protocol,
   * settings, CRF or dictionary range exists. Never the protocol's.
   */
  rangeSource?: 'default';
  defaultRange?: { min: number; max: number; unit: string; key: string; citation: string };
  /** The rule a 'family' check belongs to (EditCheckFamily). */
  family?: string;
  dictionarySource?: DictionarySource;
}

export const CATEGORY_CONFIG = {
  endpoint: { label: 'Endpoint Checks', color: 'purple', priority: 1 },
  safety: { label: 'Safety Checks', color: 'red', priority: 2 },
  protocol_compliance: { label: 'Protocol Compliance', color: 'amber', priority: 3 },
  general: { label: 'General Checks', color: 'blue', priority: 4 },
} as const;

export const SEVERITY_CONFIG = {
  error: { label: 'Error', bgClass: 'bg-red-50 text-red-700 ring-1 ring-red-600/20' },
  warning: { label: 'Warning', bgClass: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20' },
  query: { label: 'Query', bgClass: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20' },
} as const;

export const TIMING_CONFIG = {
  front_end: { label: 'Front-End', bgClass: 'bg-emerald-50 text-emerald-700' },
  back_end: { label: 'Back-End', bgClass: 'bg-slate-50 text-slate-700' },
  both: { label: 'Both', bgClass: 'bg-violet-50 text-violet-700' },
} as const;

export const CHECK_TYPE_LABELS: Record<string, string> = {
  missing_value: 'Missing Value',
  missing_page: 'Missing Page',
  range: 'Range',
  duplicate: 'Duplicate',
  intra_form: 'Intra-Form',
  cross_form: 'Cross-Form',
  external_data: 'External Data',
  protocol_violation: 'Protocol Violation',
  format: 'Format',
  calculated: 'Calculated',
};
